import React, { useEffect, useRef, useState } from "react";

export interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  label: string;
  name: string;
  value: string;
  options: Option[];
  onChange: (name: string, value: string, label: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  loading?: boolean;
  onInputChange?: (value: string) => void;
  hasRowGrid?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  name,
  value,
  options,
  onChange,
  placeholder = "Select",
  required,
  disabled = false,
  readOnly = false,
  error,
  loading,
  onInputChange,
  hasRowGrid = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectedOption = options?.find((opt) => opt?.value === value);

  const filteredOptions = options?.filter((opt) =>
    opt?.label?.toLowerCase()?.includes(search?.toLowerCase()),
  );

  /** Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
    }
  }, [isOpen]);

  // useEffect(() => {
  //   if (isOpen) {
  //     const index = filteredOptions.findIndex((item) => item.value === value);

  //     setHighlightedIndex(index >= 0 ? index : 0);
  //   }
  // }, [isOpen, value, filteredOptions]);

  useEffect(() => {
    if (isOpen && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (option: Option) => {
    onChange(name, option.value, option.label);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className="mb-3 position-relative w-100 " ref={containerRef}>
      <div className={hasRowGrid ? "row align-items-center" : ""}>
        <label
          className={`form-label ${hasRowGrid ? "col-auto mb-0 fw-semibold" : "w-100"}`}
        >
          {label} {required && <sup className="text-danger">*</sup>}
        </label>

        {/* Select Box Wrapper */}
        <div
          style={{ minWidth: "150px" }}
          // style={{ minWidth: "190px", maxWidth: "250px" }}
          className={hasRowGrid ? "col-8 col-md-4 position-relative px-0" : ""}
        >
          <div
            className={`form-select form-select-sm border ${disabled ? "bg-light" : ""} ${!disabled && error ? "border-danger form-control is-invalid" : ""} d-flex justify-content-between align-items-center  `}
            style={{
              cursor: disabled ? "not-allowed" : readOnly ? "text" : "pointer",
            }}
            onClick={() => {
              if (disabled) return;
              setIsOpen((prev) => !prev);
            }}
          >
            <span
              className={`${selectedOption ? "" : "text-muted"} text-nowrap overflow-hidden`}
              style={{
                cursor: "pointer",
              }}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          </div>
          {!disabled && error && (
            <div className="text-danger small mt-1 ms-1">{error}</div>
          )}

          {/* Dropdown */}
          {isOpen && !readOnly && (
            <div
              className="border rounded mt-1 shadow-sm bg-white position-absolute w-100"
              style={{ zIndex: 999 }}
            >
              {" "}
              {/* Search Input */}
              <div className="p-2 border-bottom">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setHighlightedIndex(0);
                    onInputChange?.(e.target.value);
                  }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setHighlightedIndex((prev) =>
                        prev < filteredOptions.length - 1 ? prev + 1 : 0,
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setHighlightedIndex((prev) =>
                        prev > 0 ? prev - 1 : filteredOptions.length - 1,
                      );
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      if (filteredOptions[highlightedIndex]) {
                        handleSelect(filteredOptions[highlightedIndex]);
                      }
                    } else if (e.key === "Escape") {
                      setIsOpen(false);
                      setSearch("");
                    }
                  }}
                />
              </div>
              {/* Options */}
              <div
                style={{ maxHeight: "160px", overflowY: "auto", zIndex: 99 }}
              >
                {loading ? (
                  <div className="px-3 py-2 text-muted">Loading...</div>
                ) : filteredOptions.length > 0 ? (
                  filteredOptions.map((opt, index) => (
                    <div
                      key={opt.value}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      className={`px-3 py-2  d-flex align-items-center ${
                        index === highlightedIndex
                          ? "dropdownHeighlightColor"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        transition: "all .15s ease",
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelect(opt)}
                    >
                      {index === highlightedIndex && (
                        <i className="ti ti-chevron-right me-2"></i>
                      )}

                      <span>{opt.label}</span>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-muted">No results found</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchableSelect;
